import React, { useCallback } from 'react';

import type { RequireSome } from '@aline/common';
import { link } from '@aline/routes';
import { useNavigate } from 'react-router-dom';
import type { ActiveCommentPanel } from 'src/editor/editor-tab-state-registry';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import type { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { useGetDocumentComments, useOnChange } from 'src/hooks';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import type { UserSubset } from 'src/types';

import { AddCommentPanel } from './AddCommentPanel';
import { CommentThreadPanel } from './CommentThreadPanel';
import { ViewCommentsPanel } from './ViewCommentsPanel';

interface ActiveCommentPanelProps {
  activeCommentPanel: ActiveCommentPanel;
  comments: CommentPanelComment[];
  commentThreadComment: CommentPanelComment | null;
}

function ActiveCommentPanelComponent({
  activeCommentPanel,
  commentThreadComment,
  comments,
}: ActiveCommentPanelProps): JSX.Element | null {
  switch (activeCommentPanel) {
    case 'ViewComments':
      return <ViewCommentsPanel comments={comments} />;
    case 'AddComment':
      return <AddCommentPanel />;
    case 'ViewThread': {
      if (!commentThreadComment) return null;

      return <CommentThreadPanel comment={commentThreadComment} />;
    }
  }
}

export function CommentsDrawer(): JSX.Element | null {
  const {
    comments: { setActivePanel, setCommentThreadId },
  } = useEditorTabStateActions();
  const activePanel = useEditorTabStateSelector(state => state.comments.activePanel);
  const { commentThreadComment, comments } = useCommentsPanelComments();
  const activeCommentPanel =
    activePanel === 'ViewThread' && !commentThreadComment ? 'ViewComments' : activePanel;

  useOnChange(() => {
    if (activePanel !== 'ViewThread' || commentThreadComment) return;

    setCommentThreadId(null);
    setActivePanel('ViewComments');
  }, [activePanel, commentThreadComment]);

  return (
    <ActiveCommentPanelComponent
      activeCommentPanel={activeCommentPanel}
      commentThreadComment={commentThreadComment}
      comments={comments}
    />
  );
}

export interface CommentPanelComment extends Pick<
  Apollo.Comment,
  | 'createdAt'
  | 'id'
  | 'isPrivate'
  | 'nodeRefId'
  | 'resolved'
  | 'text'
  | '__typename'
  | 'updatedAt'
  | 'importedAuthorName'
> {
  author: UserSubset | null;
  parent: Pick<Apollo.Comment, 'id' | '__typename'> | null;
  replies?: Pick<Apollo.Comment, 'id' | '__typename'>[];
}

function useCommentsPanelComments(): {
  comments: RequireSome<CommentPanelComment, 'nodeRefId'>[];
  commentThreadComment: CommentPanelComment | null;
} {
  const { documentId } = useDocumentParams();
  const { commentsInEditor } = useGetDocumentComments(documentId);
  const commentThreadId = useEditorTabStateSelector(state => state.comments.commentThreadId);
  const commentThreadComment =
    commentsInEditor.find(comment => comment.id === commentThreadId) ?? null;

  return {
    commentThreadComment,
    comments: commentsInEditor,
  };
}

export function useOpenComments(): {
  openAddComment: () => void;
} {
  const navigate = useNavigate();
  const {
    comments: { setActivePanel },
  } = useEditorTabStateActions();
  const { documentId } = useDocumentParams();
  const { openDrawer } = useGlobalDrawer();

  const openAddComment = useCallback(() => {
    // Set the active panel to AddComment before navigating
    setActivePanel('AddComment');
    // Navigate to comments route
    navigate(link.DocumentDetailComments({ documentId }), { replace: true });
    openDrawer();
  }, [documentId, navigate, openDrawer, setActivePanel]);

  return {
    openAddComment,
  };
}
